import { type Dayjs } from 'dayjs';
import {
  type KeyboardEvent,
  type MouseEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { buildToolbarSummaryLabel, resolveCloneDatePreview } from './tableToolbar.helpers';
import type { CloneDateQuickFilter } from '@/features/shared/hooks/useCloneDateWindow';
import type { GridColumnVisibilityModel } from '@/ui/primitives';

export type TableToolbarPopover = 'columns' | 'filters' | 'date' | 'sort' | null;
export type ToolbarHoverTarget =
  | 'columns'
  | 'filters'
  | 'date'
  | 'sort'
  | 'search'
  | 'reset'
  | null;

type TableToolbarStateParams<TSortKey extends string, TStatus extends string> = {
  onDebouncedSearchChange: (value: string) => void;
  columnVisibilityModel: GridColumnVisibilityModel;
  defaultColumnVisibilityModel: GridColumnVisibilityModel;
  columnOptions: Array<{ field: string; mandatory?: boolean }>;
  onColumnVisibilityModelChange: (nextModel: GridColumnVisibilityModel) => void;
  sortKey: TSortKey;
  sortDirection: 'asc' | 'desc';
  defaultSortKey: TSortKey;
  defaultSortDirection: 'asc' | 'desc';
  onSortChange: (sortKey: TSortKey, sortDirection: 'asc' | 'desc') => void;
  filterStatuses: TStatus[];
  onFilterStatusesChange: (next: TStatus[]) => void;
  filterEntities?: string[];
  onFilterEntitiesChange?: (next: string[]) => void;
  filterSuites: string[];
  onFilterSuitesChange?: (next: string[]) => void;
  filterTags: string[];
  onFilterTagsChange: (next: string[]) => void;
  dateQuickFilter: CloneDateQuickFilter;
  onDateQuickFilterChange: (next: CloneDateQuickFilter) => void;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  onFromDateChange: (next: Dayjs | null) => void;
  onToDateChange: (next: Dayjs | null) => void;
  summaryCount?: number | null;
  summarySingularLabel?: string;
  summaryPluralLabel?: string;
  previewFromDate?: Dayjs | null;
  previewToDate?: Dayjs | null;
};

type TableToolbarStateResult = {
  popoverState: { type: TableToolbarPopover; anchorEl: HTMLElement | null };
  searchExpanded: boolean;
  searchValue: string;
  hoverTarget: ToolbarHoverTarget;
  searchInputRef: RefObject<HTMLInputElement | null>;
  hasNonDefaultColumns: boolean;
  hasActiveFilters: boolean;
  hasActiveSearch: boolean;
  hasActiveDateFilters: boolean;
  hasActiveSort: boolean;
  hasActiveToolbarState: boolean;
  dateRangeLabel: string | null;
  displayedFromDate: Dayjs | null;
  displayedToDate: Dayjs | null;
  setHoverTarget: (target: ToolbarHoverTarget) => void;
  setSearchValue: (value: string) => void;
  openSearch: () => void;
  handleSearchBlur: () => void;
  handleSearchKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  openPopover: (
    type: Exclude<TableToolbarPopover, null>,
  ) => (event: MouseEvent<HTMLElement>) => void;
  closePopover: () => void;
  preserveWindowScroll: (action: () => void) => void;
  resetToolbarFilters: () => void;
};

export const useTableToolbarState = <TSortKey extends string, TStatus extends string>({
  onDebouncedSearchChange,
  columnVisibilityModel,
  defaultColumnVisibilityModel,
  columnOptions,
  onColumnVisibilityModelChange,
  sortKey,
  sortDirection,
  defaultSortKey,
  defaultSortDirection,
  onSortChange,
  filterStatuses,
  onFilterStatusesChange,
  filterEntities = [],
  onFilterEntitiesChange,
  filterSuites,
  onFilterSuitesChange,
  filterTags,
  onFilterTagsChange,
  dateQuickFilter,
  onDateQuickFilterChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  summaryCount = null,
  summarySingularLabel,
  summaryPluralLabel,
  previewFromDate = null,
  previewToDate = null,
}: TableToolbarStateParams<TSortKey, TStatus>): TableToolbarStateResult => {
  const [popoverState, setPopoverState] = useState<{
    type: TableToolbarPopover;
    anchorEl: HTMLElement | null;
  }>({
    type: null,
    anchorEl: null,
  });
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [hoverTarget, setHoverTarget] = useState<ToolbarHoverTarget>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const scrollRestoreTimeoutRef = useRef<number | null>(null);

  const openPopover =
    (type: Exclude<TableToolbarPopover, null>) => (event: MouseEvent<HTMLElement>) => {
      setPopoverState({ type, anchorEl: event.currentTarget });
    };

  const closePopover = (): void => {
    setPopoverState({ type: null, anchorEl: null });
  };

  const preserveWindowScroll = (action: () => void): void => {
    if (typeof window === 'undefined') {
      action();
      return;
    }
    const { scrollX, scrollY } = window;
    action();
    window.requestAnimationFrame(() => {
      window.scrollTo({ left: scrollX, top: scrollY, behavior: 'auto' });
    });
    if (scrollRestoreTimeoutRef.current !== null) {
      window.clearTimeout(scrollRestoreTimeoutRef.current);
    }
    scrollRestoreTimeoutRef.current = window.setTimeout(() => {
      window.scrollTo({ left: scrollX, top: scrollY, behavior: 'auto' });
      scrollRestoreTimeoutRef.current = null;
    }, 150);
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      onDebouncedSearchChange(searchValue);
    }, 300);
    return () => {
      window.clearTimeout(timer);
    };
  }, [onDebouncedSearchChange, searchValue]);

  useEffect(() => {
    if (!searchExpanded) return;
    window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
  }, [searchExpanded]);

  useEffect(
    () => () => {
      if (scrollRestoreTimeoutRef.current !== null) {
        window.clearTimeout(scrollRestoreTimeoutRef.current);
      }
    },
    [],
  );

  const hasNonDefaultColumns = columnOptions.some((option) => {
    if (option.mandatory) return false;
    const currentVisible = columnVisibilityModel[option.field];
    const baselineVisible = defaultColumnVisibilityModel[option.field];
    return currentVisible !== baselineVisible;
  });
  const mandatoryColumnVisibilityModel = columnOptions.reduce<GridColumnVisibilityModel>(
    (acc, option) => {
      if (option.mandatory) {
        acc[option.field] = true;
      }
      return acc;
    },
    {},
  );
  const hasActiveFilters =
    filterStatuses.length > 0 ||
    filterEntities.length > 0 ||
    filterSuites.length > 0 ||
    filterTags.length > 0;
  const hasActiveSearch = searchValue.trim().length > 0;
  const defaultDateQuickFilter: CloneDateQuickFilter = '7d';
  const hasActiveDateFilters =
    dateQuickFilter !== defaultDateQuickFilter || fromDate !== null || toDate !== null;
  const hasActiveSort = sortKey !== defaultSortKey || sortDirection !== defaultSortDirection;
  const hasActiveToolbarState =
    hasActiveFilters ||
    hasActiveDateFilters ||
    hasActiveSort ||
    hasNonDefaultColumns ||
    hasActiveSearch;
  const { displayedFromDate, displayedToDate } = resolveCloneDatePreview(
    dateQuickFilter,
    fromDate,
    toDate,
    previewFromDate,
    previewToDate,
  );
  const dateRangeLabel = buildToolbarSummaryLabel({
    count: summaryCount,
    singularLabel: summarySingularLabel,
    pluralLabel: summaryPluralLabel,
    dateQuickFilter,
    displayedFromDate,
    displayedToDate,
  });

  const resetToolbarFilters = (): void => {
    preserveWindowScroll(() => {
      setSearchValue('');
      setSearchExpanded(false);
      onFilterStatusesChange([]);
      onFilterEntitiesChange?.([]);
      onFilterSuitesChange?.([]);
      onFilterTagsChange([]);
      onDateQuickFilterChange(defaultDateQuickFilter);
      onFromDateChange(null);
      onToDateChange(null);
      onSortChange(defaultSortKey, defaultSortDirection);
      onColumnVisibilityModelChange({
        ...defaultColumnVisibilityModel,
        ...mandatoryColumnVisibilityModel,
      });
      closePopover();
    });
  };

  const openSearch = (): void => {
    setSearchExpanded(true);
  };

  const handleSearchBlur = (): void => {
    if (searchValue.trim().length > 0) return;
    setSearchExpanded(false);
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Escape') return;
    if (searchValue.length > 0) {
      setSearchValue('');
      return;
    }
    setSearchExpanded(false);
  };

  return {
    popoverState,
    searchExpanded,
    searchValue,
    hoverTarget,
    searchInputRef,
    hasNonDefaultColumns,
    hasActiveFilters,
    hasActiveSearch,
    hasActiveDateFilters,
    hasActiveSort,
    hasActiveToolbarState,
    dateRangeLabel,
    displayedFromDate,
    displayedToDate,
    setHoverTarget,
    setSearchValue,
    openSearch,
    handleSearchBlur,
    handleSearchKeyDown,
    openPopover,
    closePopover,
    preserveWindowScroll,
    resetToolbarFilters,
  };
};
